// ══════════════════════════════════════════════════════════════
// COMPASS – Backend error envelope helpers
// ══════════════════════════════════════════════════════════════
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiError } from '../models/api-error.model';

/** Fallback shown for network failures (no response from the backend). */
export const NETWORK_ERROR_KEY = 'ERROR.NETWORK';

/**
 * Reads the backend error envelope from any error, including endpoints generated with
 * `responseType: 'blob'`, whose error body arrives as a Blob.
 */
export function toApiError(err: unknown): Observable<ApiError> {
  const body = (err as { error?: unknown } | null)?.error;
  if (body instanceof Blob) {
    return from(body.text()).pipe(
      map(text => buildApiError(err, parseJson(text))),
      catchError(() => of(buildApiError(err, null))),
    );
  }
  return of(readApiError(err));
}

/** Synchronous variant for JSON responses (a Blob body yields only the status). An endpoint with no response body is
 *  generated with `responseType: 'text'`, so its error envelope arrives as JSON text: it is parsed too. */
export function readApiError(err: unknown): ApiError {
  const body = (err as { error?: unknown } | null)?.error;
  if (body instanceof Blob) return buildApiError(err, null);
  return buildApiError(err, typeof body === 'string' ? parseJson(body) : body);
}

/**
 * Text to show for an error: the backend's localized message, otherwise an i18n fallback key
 * (a network key for status 0). Never Angular's "Http failure response…" text or the English
 * HTTP reason phrase.
 */
export function errorMessage(apiError: ApiError, fallbackKey: string): string {
  const message = apiError.message?.trim();
  if (message) return message;
  return apiError.status === 0 ? NETWORK_ERROR_KEY : fallbackKey;
}

/** Error message followed by one `- field: message` line per validation error. */
export function groupedErrorMessage(apiError: ApiError, fallbackKey: string): string {
  const lines = apiError.violations?.length
    ? apiError.violations.map(v => (v.field ? `${v.field}: ${v.message}` : v.message))
    : Object.entries(apiError.fieldErrors ?? {}).map(([field, message]) => `${field}: ${message}`);
  return [errorMessage(apiError, fallbackKey), ...lines.map(line => `- ${line}`)].join('\n');
}

/**
 * `catchError` handler that turns any error into a failure action carrying the message to show:
 *   catchError(failWith('PROCESS.ERROR.LOAD_PROCESS', error => ProcessActions.loadProcessFailure({ error })))
 */
export function failWith<T>(fallbackKey: string, toAction: (message: string, apiError: ApiError) => T) {
  return (error: unknown): Observable<T> =>
    toApiError(error).pipe(map(apiError => toAction(errorMessage(apiError, fallbackKey), apiError)));
}

function buildApiError(err: unknown, body: unknown): ApiError {
  const status = err instanceof HttpErrorResponse ? err.status : undefined;
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return { status };
  }
  const envelope = body as ApiError;
  return { ...envelope, status: typeof envelope.status === 'number' ? envelope.status : status };
}

function parseJson(text: string): unknown {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}
